import styled from "@emotion/styled";
import { Link, Typography } from "@kaltura/ds-react-components";
import { Truncate } from "@kaltura/mediaspace-shared-ui";
import { EventsListItemTags } from "@kaltura/mediaspace-shared-event-platform-event-list";
import SearchResults from "../search-results/SearchResults";
import { Skeleton } from "@kaltura/mediaspace-shared-styled";

const containerPadding = 2; // in spacing units
const thumbnailDesktopHeight = 106; // in pixels
const thumbnailMobileHeight = 90; // in pixels

export const StyledMediaCard = styled("div")<{ active?: boolean }>(({ theme, active }) => ({
    display: "flex",
    padding: active ? `calc(${theme.spacing(containerPadding)}  - 2px)` : theme.spacing(containerPadding),
    gap: theme.spacing(2),
    borderRadius: theme.kaltura.shape.roundness2,
    background: theme.kaltura.palette.surfaces.paper,
    border: active ? `2px solid ${theme.kaltura.palette.tone4}` : undefined,
}));

export const StyledThumbWrap = styled("div")(({ theme }) => ({
    minWidth: 158,
    width: 158,
    minHeight: thumbnailMobileHeight,
    height: thumbnailMobileHeight,

    [theme.breakpoints.up(theme.breakpoints.values.md)]: {
        minWidth: 186,
        width: 186,
        minHeight: thumbnailDesktopHeight,
        height: thumbnailDesktopHeight,
    },
}));

export const StyledSkeleton = styled(Skeleton)(({ theme }) => ({
    borderRadius: theme.kaltura.shape.roundness2,

    // Media card's total height is the thumbnail's height + 2 container's paddings
    height: `calc(${thumbnailMobileHeight}px + ${theme.spacing(containerPadding * 2)})`,

    [theme.breakpoints.up(theme.breakpoints.values.md)]: {
        height: `calc(${thumbnailDesktopHeight}px + ${theme.spacing(containerPadding * 2)})`,
    },
}));

export const ScheduledBadgeWrap = styled("div")(({ theme }) => ({
    lineHeight: theme.spacing(2),
}));

export const StyledInfo = styled("div")({
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
});

export const StyledLink = styled(Link)(({ theme }) => ({
    textDecoration: "none",
    display: "block",
    marginBottom: theme.spacing(1),
}));
export const StyledName = styled(Typography)(({ theme }) => ({
    ...theme.kaltura.typography.heading5,
    color: theme.kaltura.palette.tone1,
    wordBreak: "break-word",
    overflowWrap: "break-word",
    whiteSpace: "normal",
    maxWidth: "100%",
    "&:hover, &:active, &:focus": {
        color: theme.kaltura.palette.tone1,
    },
}));

export const StyledDescription = styled(Truncate)(({ theme, lines = 0 }) => ({
    ...theme.kaltura.typography.body2,
    color: theme.kaltura.palette.tone2,

    "&.truncation-error": {
        maxHeight: `${lines * 1.5}em`,
        overflow: "hidden",
    },
    "& a": {
        fontWeight: theme.kaltura.typography.fontWeightBold,
        textDecoration: "none",
    },
}));

export const StyledTags = styled(
    EventsListItemTags,
    {
        shouldForwardProp(propName: PropertyKey): boolean {
            return propName !== "shortGap";
        },
    }
)<{ shortGap?: boolean }>(({ theme, shortGap }) => ({
    marginTop: theme.spacing(2),
    padding: 0,
    ...(
        shortGap && {
            marginTop: theme.spacing(1.5),
        }
    ),
}));

export const StyledSearchResults = styled(SearchResults)({
    "&.kms-ds-mediacard-search-results-summary": {
        flexGrow: 2,
    },
});

export const StyledSearchResultsWrap = styled(
    "div",
    {
        shouldForwardProp(propName: PropertyKey): boolean {
            return propName !== "shortGap";
        },
    }
)<{ shortGap?: boolean }>(({ theme, shortGap }) => ({
    display: "flex",
    marginTop: theme.spacing(2),
    padding: 0,
    ...(
        shortGap && {
            marginTop: theme.spacing(1.5),
        }
    ),
}));

export const StyledExploreWrap = styled(
    "div",
    {
        shouldForwardProp(propName: PropertyKey): boolean {
            return propName !== "shortGap";
        },
    }
)<{ shortGap: boolean }>(({ theme, shortGap }) => ({
    display: "flex",
    marginTop: theme.spacing(2),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
        marginTop: theme.spacing(1),
    },
    padding: 0,

    ...(
        shortGap && {
            marginTop: theme.spacing(1.5),
            [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
                marginTop: theme.spacing(-1.5),
            },
        }
    ),
    alignItems: "flex-end",
    justifyContent: "flex-end",
    minWidth: 66,
}));
